import { useEffect, useState, useCallback } from 'react';
import { Card } from '@ftedn/ui';
import { useApi } from '../../hooks/useApi';

export function AnalyticsPage() {
  const { get, post } = useApi();
  const [tab, setTab] = useState<'retention' | 'stickiness' | 'experiments'>('retention');
  const [retention, setRetention] = useState<any>(null);
  const [stickiness, setStickiness] = useState<any>(null);
  const [experiments, setExperiments] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadRetention = useCallback(async () => {
    try { setRetention((await get('/analytics-v2/retention')).retention); } catch (e) { setError(String(e)); }
  }, []);
  const loadStickiness = useCallback(async () => {
    try { setStickiness((await get('/analytics-v2/stickiness')).stickiness); } catch (e) { setError(String(e)); }
  }, []);
  const loadExperiments = useCallback(async () => {
    try { setExperiments((await get('/analytics-v2/experiments')).experiments || []); } catch (e) { setError(String(e)); }
  }, []);

  useEffect(() => {
    if (tab === 'retention') loadRetention();
    else if (tab === 'stickiness') loadStickiness();
    else loadExperiments();
  }, [tab]);

  if (error) return <Card><p style={{ color: 'var(--color-error)' }}>Error: {error}</p></Card>;

  return (
    <div>
      <h1>📊 Analytics & Experiments</h1>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {(['retention', 'stickiness', 'experiments'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
            fontWeight: tab === t ? 700 : 500, fontSize: '0.85rem',
            background: tab === t ? 'var(--color-primary, #18181b)' : 'var(--color-surface-2, #f3f4f6)',
            color: tab === t ? '#fff' : 'var(--color-text, #111)',
          }}>
            {t === 'retention' ? '📈 Retention' : t === 'stickiness' ? '🔥 Stickiness' : '🧪 Experiments'}
          </button>
        ))}
      </div>

      {tab === 'retention' && retention && (
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
          {(['d1', 'd7', 'd30'] as const).map(period => (
            <Card key={period}>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '0 0 4px' }}>
                {period === 'd1' ? 'Day 1' : period === 'd7' ? 'Day 7' : 'Day 30'} Retention
              </p>
              <p style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>
                {retention[period]?.rate}%
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
                {retention[period]?.active} active / {retention.totalUsers} total
              </p>
            </Card>
          ))}
        </div>
      )}

      {tab === 'stickiness' && stickiness && (
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
          <Card>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '0 0 4px' }}>DAU (Today)</p>
            <p style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>{stickiness.dau}</p>
          </Card>
          <Card>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '0 0 4px' }}>Avg Streak</p>
            <p style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>{stickiness.streakAvg} days</p>
          </Card>
          <Card>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '0 0 4px' }}>Active Streaks</p>
            <p style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>{stickiness.activeStreaks}</p>
          </Card>
        </div>
      )}

      {tab === 'experiments' && (
        <>
          {experiments.length === 0 ? (
            <Card><p style={{ color: 'var(--color-text-secondary)' }}>No experiments yet</p></Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {experiments.map((exp: any) => (
                <Card key={exp.name}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h3 style={{ margin: 0 }}>{exp.name}</h3>
                    <span style={{
                      padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                      background: exp.active ? '#10b98120' : '#6b728020',
                      color: exp.active ? '#10b981' : '#6b7280',
                    }}>
                      {exp.active ? 'ACTIVE' : 'ENDED'}
                    </span>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', margin: '0 0 0.5rem' }}>
                    {exp.description || 'No description'} • Metric: <strong>{exp.metric}</strong>
                  </p>
                  <p style={{ fontSize: '0.8rem' }}>
                    Variants: {exp.variants?.map((v: string) => (
                      <span key={v} style={{
                        padding: '2px 8px', borderRadius: 6, fontSize: '0.75rem',
                        background: 'var(--color-surface-2, #f3f4f6)', marginRight: '4px',
                      }}>{v}</span>
                    ))}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
